/**
 * Pure helpers for the Inquiry Item Selection_Wizard (inquiry-module-redesign):
 * filtering the Products/Services steps, toggling multi-selection, computing
 * "connected supplier" badges, gating step navigation / Save, and
 * aggregating the final per-supplier summary shown on the Inquiry Item row.
 *
 * The Products step sources from `Supplier_Product` catalog records (the
 * same data the Supplier Products tab shows — Requirement 4). Services
 * source from their own dedicated catalog (`initialServiceItems` — the same
 * data the Products Module's Services tab shows), owned by a single
 * supplier and with no category of its own.
 *
 * The Logistics step is a free-text note (`wizardResult.logisticsNotes`),
 * not a catalogue selection — it carries no supplier of its own and so is
 * outside `buildSupplierSummary`'s per-supplier roles.
 *
 * A `SupplierProductRef` (the shape stored in wizardResult.selectedProducts /
 * selectedServices) looks like:
 *   { supplierProductId, supplierId, supplierName, productId, productName }
 */

export const WIZARD_STEPS = ['Products', 'Services', 'Logistics'];

const supplierNameById = (supplierId, suppliers = []) => (
  suppliers.find((s) => s.id === supplierId)?.name || ''
);

/**
 * Builds a SupplierProductRef from a raw Supplier_Product record, carrying
 * every display detail (price fields, supplier item number, description,
 * status, category) alongside the identifying fields — one record is one
 * selectable row on every step (Products included: a product offered by
 * two suppliers is two rows, not one row listing two supplier names).
 */
export const toSupplierProductRef = (sp, suppliers = []) => ({
  supplierProductId: sp.id,
  supplierId: sp.supplierId,
  supplierName: supplierNameById(sp.supplierId, suppliers),
  productId: sp.productId ?? null,
  productName: sp.productName || sp.description || '',
  categoryId: sp.categoryId ?? null,
  supplierItemNumber: sp.supplierItemNumber || '',
  description: sp.description || '',
  avgItemPrice: sp.avgItemPrice ?? null,
  avgBrandingPrice: sp.avgBrandingPrice ?? null,
  avgDeliveryPricePerItemPerKm: sp.avgDeliveryPricePerItemPerKm ?? null,
  status: sp.status || 'Active',
});

// ─────────────────────────────────────────────────────────────────────────
// Products step — one row per (product, supplier) Supplier_Product record,
// scoped to the Inquiry_Item's category (Requirement 4).
// ─────────────────────────────────────────────────────────────────────────

/** Supplier_Product records qualifying for the Products step. */
export const getQualifyingProductRecords = (supplierProducts = [], categoryId) => (
  (supplierProducts || []).filter((sp) => sp.categoryId === categoryId && sp.avgItemPrice != null)
);

// ─────────────────────────────────────────────────────────────────────────
// Services / Logistics steps — one row per `initialServiceItems` /
// `initialLogisticsItems` catalog entry (the Products Module's own Services
// / Logistics lists), not scoped to the item's category: those catalogs
// have no category of their own — a decoration or freight service isn't
// tied to one product category the way a product is.
// ─────────────────────────────────────────────────────────────────────────

/** Builds a SupplierProductRef from a Service/Logistics catalog entry (single-supplier-owned, no price field). */
export const toCatalogItemRef = (entry, suppliers = []) => ({
  supplierProductId: entry.id,
  supplierId: entry.supplierId,
  supplierName: supplierNameById(entry.supplierId, suppliers),
  productId: null,
  productName: entry.name,
  code: entry.code || '',
  serviceType: entry.serviceType || null,
  pricingUnit: entry.pricingUnit || '',
  status: entry.status || 'Active',
});

/** Toggles a single ref — every step's rows are 1:1 with a catalog record. */
export const toggleRefSelection = (selected = [], ref) => (
  selected.some((entry) => entry.supplierProductId === ref.supplierProductId)
    ? selected.filter((entry) => entry.supplierProductId !== ref.supplierProductId)
    : [...selected, ref]
);

// ─────────────────────────────────────────────────────────────────────────
// Search (case-insensitive substring) — matches each step's own Products
// Module list: Products searches by product number/name/supplier, Services
// and Logistics search by code/name.
// ─────────────────────────────────────────────────────────────────────────

const includesQuery = (value, query) => String(value || '').toLowerCase().includes(query);

export const filterProductRows = (rows = [], searchText = '') => {
  const query = String(searchText || '').trim().toLowerCase();
  if (!query) return rows;
  return rows.filter((row) => (
    includesQuery(row.productName, query)
    || includesQuery(row.supplierName, query)
    || includesQuery(row.supplierItemNumber, query)
  ));
};

export const filterCatalogRows = (rows = [], searchText = '') => {
  const query = String(searchText || '').trim().toLowerCase();
  if (!query) return rows;
  return rows.filter((row) => includesQuery(row.productName, query) || includesQuery(row.code, query));
};

// ─────────────────────────────────────────────────────────────────────────
// Connected-supplier badges — true iff `supplierId` matches the supplier of
// at least one ref in any of the given selection lists (Services: checked
// against selected products — Property 8).
// ─────────────────────────────────────────────────────────────────────────

export const hasSupplierConnection = (supplierId, ...refLists) => (
  refLists.some((refs) => (refs || []).some((ref) => ref.supplierId === supplierId))
);

// ─────────────────────────────────────────────────────────────────────────
// Step navigation lock rules (Property 7)
// ─────────────────────────────────────────────────────────────────────────

/** A step is navigable iff every step before it is marked completed. */
export const isStepNavigable = (stepIndex, completedSteps = new Set()) => {
  for (let i = 0; i < stepIndex; i += 1) {
    if (!completedSteps.has(i)) return false;
  }
  return true;
};

/** Maps wizard state to an Ant Design <Steps> status for a given step. */
export const getStepStatus = (stepIndex, currentStep, completedSteps = new Set()) => {
  if (stepIndex === currentStep) return 'process';
  if (completedSteps.has(stepIndex)) return 'finish';
  return 'wait';
};

// ─────────────────────────────────────────────────────────────────────────
// Save gate (Property 10) — Logistics is a free-text note, so its gate is
// "non-blank" rather than "non-empty selection".
// ─────────────────────────────────────────────────────────────────────────

export const isWizardSaveEnabled = (selectedProducts = [], selectedServices = [], logisticsNotes = '') => (
  selectedProducts.length > 0 && selectedServices.length > 0 && String(logisticsNotes || '').trim().length > 0
);

// ─────────────────────────────────────────────────────────────────────────
// Wizard open/restore state (Property 3 — round-trip)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Computes the SelectionWizard's initial state when it opens for a given
 * Inquiry_Item. Since Save is gated on all three steps being non-empty
 * (isWizardSaveEnabled), a saved `wizardResult` always means every step was
 * already completed, so "resume at the last step reached" (Requirement 3.4)
 * is simply the Logistics step with Products/Services marked complete.
 * No prior selections -> a fresh wizard starting at the Products step
 * (Requirement 3.3).
 */
export const getInitialWizardState = (item) => {
  const wizardResult = item?.wizardResult;
  if (wizardResult) {
    return {
      selectedProducts: wizardResult.selectedProducts || [],
      selectedServices: wizardResult.selectedServices || [],
      logisticsNotes: wizardResult.logisticsNotes || '',
      completedSteps: new Set([0, 1]),
      currentStep: 2,
    };
  }
  return {
    selectedProducts: [],
    selectedServices: [],
    logisticsNotes: '',
    completedSteps: new Set(),
    currentStep: 0,
  };
};

// ─────────────────────────────────────────────────────────────────────────
// Supplier summary aggregation (Property 11)
// ─────────────────────────────────────────────────────────────────────────

const SUMMARY_ROLE_SOURCES = [
  { key: 'selectedProducts', role: 'Manufacturing' },
  { key: 'selectedServices', role: 'Services' },
];

/**
 * Groups wizard selections by supplierId, annotating each with the role
 * labels (Manufacturing / Services) applicable to that supplier. Logistics
 * has no supplier of its own (it's a free-text note — see `logisticsNotes`
 * on the wizard result) and so isn't part of this aggregation.
 * @returns {Array<{supplierId, supplierName, roles: string[]}>}
 */
export const buildSupplierSummary = (selectedProducts = [], selectedServices = [], suppliers = []) => {
  const bySupplier = new Map();
  const sources = { selectedProducts, selectedServices };

  SUMMARY_ROLE_SOURCES.forEach(({ key, role }) => {
    (sources[key] || []).forEach((ref) => {
      if (!bySupplier.has(ref.supplierId)) {
        bySupplier.set(ref.supplierId, {
          supplierId: ref.supplierId,
          supplierName: ref.supplierName || supplierNameById(ref.supplierId, suppliers),
          roles: [],
        });
      }
      const entry = bySupplier.get(ref.supplierId);
      if (!entry.roles.includes(role)) entry.roles.push(role);
    });
  });

  return Array.from(bySupplier.values());
};
